"use client";

import type { MutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import gql from "graphql-tag";
import type {
  CanvasSchedulerResponse,
  RescheduleCanvasAppointmentInput,
} from "@rmh/defs";
import type { GeneratedMutation } from "@aws-amplify/api-graphql";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

interface RescheduleCanvasAppointmentVariables {
  input: RescheduleCanvasAppointmentInput;
}

interface RescheduleCanvasAppointmentResponse {
  data?: {
    rescheduleCanvasAppointment: CanvasSchedulerResponse;
  };
}

export const rescheduleCanvasAppointmentMutation = gql`
  mutation rescheduleCanvasAppointment(
    $input: RescheduleCanvasAppointmentInput!
  ) {
    rescheduleCanvasAppointment(input: $input) {
      success
      error {
        type
        message
        details
      }
      data {
        appointmentId
        canvasAppointmentId
        canvasPractitionerId
        appointmentDateTime
        timestamp
      }
    }
  }
`.loc?.source.body as GeneratedMutation<
  RescheduleCanvasAppointmentVariables,
  {
    rescheduleCanvasAppointment: CanvasSchedulerResponse;
  }
>;

export const useRescheduleCanvasAppointment = (
  options: MutationOptions<
    RescheduleCanvasAppointmentResponse,
    Error,
    RescheduleCanvasAppointmentVariables
  > = {}
) => {
  const { mutateAsync, isPending } = useMutation<
    RescheduleCanvasAppointmentResponse,
    Error,
    RescheduleCanvasAppointmentVariables
  >({
    ...options,
    mutationFn: async (variables) =>
      fetchGraphQLClient(rescheduleCanvasAppointmentMutation, variables),
  });

  const rescheduleCanvasAppointment = async (
    input: RescheduleCanvasAppointmentInput
  ): Promise<CanvasSchedulerResponse> => {
    const response = await mutateAsync({ input });
    const payload = response?.data?.rescheduleCanvasAppointment;

    if (!payload) {
      throw new Error("Empty response from rescheduleCanvasAppointment");
    }

    return payload;
  };

  return {
    rescheduleCanvasAppointment,
    isPending,
  };
};
